// Site language. Spanish (/es) and Brazilian Portuguese (/pt) started in Oct
// 2026: Spanish-speaking countries were 17.6% of Google clicks but ~9% of
// charges, Brazil 4.2% of clicks.
//
// The URL decides the language: Next i18n routing (next.config.mjs) serves
// every page at /, /es/... and /pt/..., server-rendered in that language, and
// next/link keeps visitors inside their prefix. middleware.js sends people
// whose saved choice (jm_lang cookie) or browser language is Spanish or
// Portuguese from the unprefixed URL to theirs; crawlers are never redirected.
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import { en, es, pt, type MessageKey } from "./messages";

export type Locale = "en" | "es" | "pt";
export const LOCALES: Locale[] = ["en", "es", "pt"];
export type { MessageKey };

// hreflang / <html lang> / og:locale per site locale. The Portuguese copy is
// written for Brazil but is the only Portuguese we have, so it is offered to
// every Portuguese searcher ("pt"), and the Spanish to every Spanish one.
export const LOCALE_META: Record<
  Locale,
  { hreflang: string; htmlLang: string; og: string }
> = {
  en: { hreflang: "en", htmlLang: "en", og: "en_US" },
  es: { hreflang: "es", htmlLang: "es", og: "es_LA" },
  pt: { hreflang: "pt", htmlLang: "pt-BR", og: "pt_BR" },
};

const MESSAGES: Record<Locale, Partial<Record<MessageKey, string>>> = {
  en,
  es,
  pt,
};

// A missing translation falls back to English rather than showing a key.
export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
) {
  let s = MESSAGES[locale]?.[key] ?? en[key] ?? key;
  if (vars)
    for (const [k, v] of Object.entries(vars))
      s = s.split(`{${k}}`).join(String(v));
  return s;
}

export function asLocale(l: string | undefined | null): Locale {
  return l === "es" || l === "pt" ? l : "en";
}

// Locale implied by a path: /es, /es/... -> "es". Anything else is English.
export function localeFromPath(path: string | undefined | null): Locale {
  const m = /^\/(es|pt)(?:[/?#]|$)/.exec(path || "");
  return m ? (m[1] as Locale) : "en";
}

// Saved language choice, read by middleware.js. Set by the footer switcher.
export const LANG_COOKIE = "jm_lang";

interface Ctx {
  locale: Locale;
  // True on /es and /pt pages (kept for callers that branch on it).
  fromUrl: boolean;
  // Saves the choice and moves to the same page in that language.
  setLocale: (l: Locale) => void;
}
const LocaleContext = createContext<Ctx>({
  locale: "en",
  fromUrl: false,
  setLocale: () => {},
});

// For code outside React (e.g. a helper that opens a modal with a message).
// Kept in step by LocaleProvider; "en" on the server.
let currentLocale: Locale = "en";
export const getCurrentLocale = () => currentLocale;

export function LocaleProvider({
  urlLocale,
  children,
}: {
  urlLocale: Locale;
  children: React.ReactNode;
}) {
  const router = useRouter();
  const value = useMemo<Ctx>(
    () => ({
      locale: urlLocale,
      fromUrl: urlLocale !== "en",
      setLocale: (l) => {
        try {
          document.cookie = `${LANG_COOKIE}=${l}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
        } catch {
          /* cookies blocked */
        }
        if (l !== urlLocale)
          router.push(router.asPath, undefined, { locale: l });
      },
    }),
    [urlLocale, router],
  );
  useEffect(() => {
    currentLocale = urlLocale;
  }, [urlLocale]);
  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export const useLocale = () => useContext(LocaleContext);

// t() for the page's locale. Pass `fixed` for content that must stay in one
// language whatever the URL, e.g. a landing page's own copy.
export function useT(fixed?: Locale) {
  const { locale } = useLocale();
  const l = fixed ?? locale;
  return useMemo(
    () => (key: MessageKey, vars?: Record<string, string | number>) =>
      translate(l, key, vars),
    [l],
  );
}
